import { useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { inNativeApp, isNativePhone } from '../lib/nativeApp'
import useNode, { isNodeOffline } from '../models/useNode'
import usePhoneNode, { usePhoneNodeId } from '../models/usePhoneNode'
import { approvalPath, phoneNodeView } from '../utilities/phoneNode'
import { nodePagePath } from '../utilities/nodeGroups'
import IconNode from './IconNode'
import ListItemLink from './ListItemLink'

const POLL_MS = 1500

type Props = {
  /** On the Nodes list: a registered phone is its first row, and the Add card can be dismissed. */
  onList?: boolean
}

/**
 * This phone, inside the noBGP app only. Add turns the node on (NobgpShell.nodeStart, which takes
 * no key). The agent then registers as `nobgp register` does on any other machine, and the console
 * opens its registration page, where the person approves the phone and chooses the network and the
 * name (utilities/phoneNode.ts). Once registered, the Nodes list shows the phone as its first row;
 * its node page removes it (Node.tsx).
 */
export default function AddThisPhone({ onList }: Readonly<Props>) {
  const { status, removing, cardHidden, refresh, start, remove, hideCard } = usePhoneNode()
  const phoneId = usePhoneNodeId()
  const node = useNode(s => (phoneId ? s.nodes.find(n => n.id === phoneId) : undefined))
  // Set by the button: the registration page then opens by itself. A page opened later, while a
  // registration waits, offers it instead.
  const started = useRef(false)
  const view = phoneNodeView(status, window.location.origin)
  const polling = !removing && (view === 'waiting' || view === 'approve')

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (!polling) return
    const timer = window.setInterval(() => void refresh(), POLL_MS)
    return () => window.clearInterval(timer)
  }, [polling, refresh])

  const approve = approvalPath(status?.approve_url, window.location.origin)
  useEffect(() => {
    if (approve && started.current) window.location.assign(approve)
  }, [approve])

  if (!inNativeApp || (view === 'none' && !removing)) return null
  const device = isNativePhone ? 'phone' : 'tablet'

  const add = () => {
    started.current = true
    void start()
  }

  if (onList && node) {
    const off = view === 'stopped' || view === 'failed'
    return (
      <Box sx={{ mb: 2 }}>
        <ListItemLink
          to={nodePagePath(node.id)}
          icon={<IconNode type="node" offline={isNodeOffline(node)} size={20} />}
        >
          <Typography variant="body2">{node.name}</Typography>
          <Typography variant="caption" color="textSecondary" component="div">
            This {device} · {off ? 'its VPN is off' : isNodeOffline(node) ? 'Offline' : 'Online'}
          </Typography>
        </ListItemLink>
        {off && (
          <Button variant="text" size="small" onClick={add}>
            Turn on
          </Button>
        )}
        <Divider sx={{ mt: 1 }} />
      </Box>
    )
  }
  if (onList && cardHidden && view === 'off') return null

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
        This {device}
      </Typography>
      {removing && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <CircularProgress size={16} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Removing this {device}…
          </Typography>
        </Stack>
      )}
      {!removing && view === 'node' && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          This {device} is a node.
        </Typography>
      )}
      {!removing && view === 'waiting' && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <CircularProgress size={16} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Starting the node…
          </Typography>
        </Stack>
      )}
      {!removing && view === 'approve' && approve && (
        <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This {device} waits for you to approve it.
          </Typography>
          <Button variant="contained" size="small" onClick={() => window.location.assign(approve)}>
            Approve this {device}
          </Button>
        </Stack>
      )}
      {!removing && (view === 'off' || view === 'stopped' || view === 'failed') && (
        <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {view === 'off' &&
              `Make this ${device} a node. The ${device} then asks to allow a VPN: allow it. You then approve the ${device} and choose its network and name.`}
            {view === 'stopped' && `The node on this ${device} is off. Its VPN is off or was not allowed.`}
            {view === 'failed' &&
              `The node could not start${status?.error ? `: ${status.error}` : '.'} Tap Try again, and allow the VPN.`}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" onClick={add}>
              {view === 'off' ? `Add this ${device}` : 'Try again'}
            </Button>
            {onList && view === 'off' && (
              <Button variant="text" size="small" color="inherit" onClick={hideCard}>
                Don't show again
              </Button>
            )}
          </Stack>
        </Stack>
      )}
      {!removing && view !== 'off' && (
        <Button
          variant="text"
          size="small"
          color="error"
          sx={{ mt: 1, ml: -1 }}
          onClick={() => {
            if (window.confirm(`Remove this ${device} from noBGP? Its node is deleted and its VPN is removed.`))
              void remove()
                .then(() => (started.current = false))
                .catch((e: unknown) => window.alert(e instanceof Error ? e.message : 'Could not remove the node.'))
          }}
        >
          Remove this {device}
        </Button>
      )}
      <Divider sx={{ mt: 3 }} />
    </Box>
  )
}
